import type { LocalDateTime, SupervisorReminder } from '@/data/db';

/**
 * 瀏覽器通知（spec 15.2）。
 *
 * 關鍵原則：**桌面通知失敗、被拒絕或未授權，都不改變站內提醒的狀態。**
 * 這裡只負責「順便跳一個桌面通知」，提醒的生命週期完全由資料層決定。
 */

/** 通知去重的鍵；包含到期時間，稍後之後會被當成新的一次。 */
export function notificationKey(reminder: SupervisorReminder): string {
  return `${reminder.id}@${reminder.snoozedUntilLocal ?? reminder.remindAtLocal}`;
}

export function selectDueReminders(
  reminders: readonly SupervisorReminder[],
  nowLocal: LocalDateTime,
  alreadyNotified: ReadonlySet<string>,
): SupervisorReminder[] {
  return reminders.filter((reminder) => {
    if (reminder.state !== 'pending' && reminder.state !== 'snoozed') return false;
    const dueAt = reminder.snoozedUntilLocal ?? reminder.remindAtLocal;
    if (dueAt > nowLocal) return false;
    return !alreadyNotified.has(notificationKey(reminder)) && !alreadyNotified.has(reminder.id);
  });
}

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function notificationPermission(): NotificationPermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as NotificationPermissionState;
}

/** 使用者主動授權後才啟用（spec 15.2）。 */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return (await Notification.requestPermission()) as NotificationPermissionState;
  } catch {
    return 'denied';
  }
}

export interface ShowNotificationInput {
  title: string;
  body: string;
  tag: string;
  onClick?: () => void;
}

/** 顯示桌面通知。失敗時安靜返回 false，絕不影響站內提醒。 */
export function showDesktopNotification(input: ShowNotificationInput): boolean {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

  try {
    const notification = new Notification(input.title, {
      body: input.body,
      tag: input.tag,
      icon: '/favicon.svg',
    });
    if (input.onClick) {
      notification.onclick = () => {
        window.focus();
        input.onClick?.();
        notification.close();
      };
    }
    return true;
  } catch {
    return false;
  }
}

export const NOTIFICATION_CAVEAT =
  '網站關閉、瀏覽器關閉、裝置休眠或系統限制時，桌面通知不保證送達。' +
  '站內提醒不會因此消失，隨時可以在提醒中心找到。';
